// src/chat/DigiBoardPanel.tsx
// -------------------------------------------------------
// Refactored DigiBoardPanel with profile-aware rendering.
// Renders all existing block types + new DT-specific blocks.
// -------------------------------------------------------

import React from "react";
import { getCTBoardForPlayback, getPlaybackState } from "./lessonStore";
import type { LessonBlueprint } from "./lessonStore";

type Role = "system" | "user" | "assistant";
export type Msg = { role: Role; content: string };

// ── Shared block types (existing + new) ──────────────────

type Block =
  | { type: "hero"; title: string; sub?: string; content?: any }
  | { type: "chiprow"; chips: string[]; content?: any }
  | { type: "fill"; title: string; lines: string[]; content?: any }
  | { type: "checklist"; title: string; items: Array<{ text: string; status?: "active" | "confirmed" | "discarded"; note?: string }>; content?: any }
  | { type: "grid"; columns: 2 | 3 | 4; items: Array<{ badge?: string; title: string; body: string }>; content?: any }
  | { type: "callout"; text: string; content?: any }
  // DT-specific blocks
  | { type: "assumption"; content: { items: Array<{ text: string; status: "active" | "confirmed" | "discarded"; note?: string }> } }
  | { type: "clue"; content: { items: Array<{ text: string; unlocked: boolean; source: string }> } }
  | { type: "consequence"; content: { text: string; success: boolean; explanation: string } }
  | { type: "reflection"; content: { learned: string[]; missed: string[]; principle: string } }
  | { type: "nextcheck"; content: { text: string } };

type DigiBoard =
  | {
      version: number;
      scene?: { title?: string; unit?: string };
      meta?: { mode?: "question" | "explanation" };
      blocks: Block[];
      layout?: string;
      phase?: string;
    }
  | null;

type Props = {
  systemPrompt: string;
  messages: Msg[];
  digiboardUrl?: string;
  lessonTitleFallback?: string;

  boardOverride?: DigiBoard;
  forcedMode?: "intro" | "question" | "explanation";
  activeLesson?: LessonBlueprint | null;

  /** The active GameProfile id (e.g. 'kcv' | 'design-thinking') */
  profileId?: string;
  /** Current game phase */
  gamePhase?: string;
};

const DEFAULT_DIGIBOARD_URL =
  (process.env.REACT_APP_DIGIBOARD_URL as string) || "/api/digiboard.php";

// ── Utility functions ────────────────────────────────────

function clean(s: string) {
  return (s || "").replace(/\s+/g, " ").trim();
}
function safeTrim(s: string, max = 140) {
  const t = clean(s);
  if (!t) return "";
  return t.length > max ? t.slice(0, max).trim() + "\u2026" : t;
}
function safeArray(a: any, max = 6, eachMax = 60): string[] {
  if (!Array.isArray(a)) return [];
  return a
    .map((x: any) => safeTrim(String(x ?? ""), eachMax))
    .filter(Boolean)
    .slice(0, max);
}

function parseLessonTitleAndTopics(systemPrompt: string) {
  const text = systemPrompt || "";
  const titleMatch = text.match(/LESSON TITLE:\s*([^\n]+)/i);
  const lessonTitle = (titleMatch?.[1] || "").trim();

  const introBlockMatch = text.match(
    /INTRO TOPICS\s*\(DIGIBOARD\):([\s\S]*?)\n\s*(?:LESSON TITLE:|={3,}|BELANGRIJK|WERKWIJZE|START|IMPORTANT|MODUS-SYSTEEM|EXAM-QUESTION RULES)/i
  );
  const block = introBlockMatch?.[1] || "";
  const topics = block
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => l.replace(/^-+\s*/, "").trim())
    .filter(Boolean);

  return { lessonTitle, topics };
}

function isOnboardingQuestion(q: string) {
  const t = clean(q).toLowerCase();
  if (t === "klaar?" || t === "klaar voor?" || t.includes("klaar voor")) return true;
  if (t.includes("wil je door")) return true;
  if (t.includes("zal ik doorgaan")) return true;
  if (t.includes("zal ik verder")) return true;
  if (t.includes("begrijp je")) return true;
  if (t.includes("snap je")) return true;
  return false;
}

function extractLastMeaningfulQuestion(assistantText: string): string | null {
  const tail = clean(assistantText);
  if (!tail.includes("?")) return null;

  const parts = tail.split("?");
  for (let i = parts.length - 2; i >= 0; i--) {
    const q = clean(parts[i]);
    if (q.length < 6) continue;
    const candidate = q + "?";
    if (isOnboardingQuestion(candidate)) continue;
    if (candidate.length >= 10) return candidate;
  }
  return null;
}

function splitExplanationAndQuestion(assistantText: string) {
  const q = extractLastMeaningfulQuestion(assistantText);
  if (!q) return { explanation: clean(assistantText), question: null as string | null };

  const idx = assistantText.lastIndexOf(q);
  if (idx === -1) return { explanation: clean(assistantText), question: q };

  const explanation = assistantText.slice(0, idx).trim();
  return { explanation: clean(explanation), question: clean(q) };
}

// ── Shared block renderers ──────────────────────────────

function TopicChips({ topics }: { topics: string[] }) {
  const list = topics.slice(0, 12);
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
      {list.map((t, i) => (
        <div key={i} style={{ padding: "12px 14px", borderRadius: 999, background: "rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.06)", fontSize: 15, fontWeight: 900 }}>
          {safeTrim(t, 72)}
        </div>
      ))}
    </div>
  );
}

function ChipRow({ chips }: { chips: string[] }) {
  const list = safeArray(chips, 6, 40);
  if (!list.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 16 }}>
      {list.map((c, i) => (
        <div key={i} style={{ padding: "10px 12px", borderRadius: 999, background: "rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.06)", fontSize: 14, fontWeight: 900 }}>
          {c}
        </div>
      ))}
    </div>
  );
}

function FillCard({ title, lines }: { title: string; lines: string[] }) {
  const safeLines = safeArray(lines, 6, 90);
  if (!safeLines.length) return null;
  return (
    <div style={{ marginTop: 18, padding: 18, borderRadius: 18, background: "rgba(255,255,255,0.72)", border: "1px dashed rgba(0,0,0,0.22)" }}>
      <div style={{ fontWeight: 950, marginBottom: 12, fontSize: 16 }}>{safeTrim(title, 60)}</div>
      <div style={{ display: "grid", gap: 12 }}>
        {safeLines.map((l, i) => (
          <div key={i} style={{ fontSize: 18, fontWeight: 850, opacity: 0.92 }}>
            {safeTrim(l, 110)}
            <div style={{ height: 10, borderBottom: "2px solid rgba(0,0,0,0.18)", marginTop: 6 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function Checklist({ title, items }: { title: string; items: Array<{ text: string; status?: "active" | "confirmed" | "discarded"; note?: string }> }) {
  const list = Array.isArray(items) ? items.slice(0, 10) : [];
  if (!list.length) return null;
  return (
    <div style={{ marginTop: 18, padding: 18, borderRadius: 20, background: "rgba(255,255,255,0.72)", border: "1px solid rgba(0,0,0,0.08)", boxShadow: "0 8px 20px rgba(0,0,0,0.04)" }}>
      <div style={{ fontWeight: 950, marginBottom: 12, fontSize: 16 }}>{safeTrim(title, 60)}</div>
      <div style={{ display: "grid", gap: 10 }}>
        {list.map((it, i) => {
          const status = it.status || "active";
          const isDiscarded = status === "discarded";
          const isConfirmed = status === "confirmed";
          return (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", borderRadius: 14, background: isDiscarded ? "rgba(220,38,38,0.06)" : isConfirmed ? "rgba(34,197,94,0.08)" : "rgba(0,0,0,0.04)", border: `1px solid ${isDiscarded ? "rgba(220,38,38,0.15)" : isConfirmed ? "rgba(34,197,94,0.2)" : "rgba(0,0,0,0.06)"}` }} title={it.note ? safeTrim(it.note, 160) : undefined}>
              <div style={{ width: 26, textAlign: "center", fontSize: 16, lineHeight: 1.3, fontWeight: 900, opacity: 0.7 }}>
                {isConfirmed ? "V" : isDiscarded ? "X" : "?"}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 18, fontWeight: 900, opacity: isDiscarded ? 0.55 : 0.92, textDecoration: isDiscarded ? "line-through" : "none", lineHeight: 1.2 }}>
                  {safeTrim(it.text, 120)}
                </div>
                {it.note ? <div style={{ marginTop: 4, fontSize: 14, fontWeight: 850, opacity: 0.6 }}>{safeTrim(it.note, 140)}</div> : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Grid({ columns, items }: { columns: 2 | 3 | 4; items: Array<{ badge?: string; title: string; body: string }> }) {
  const list = Array.isArray(items) ? items.slice(0, 6) : [];
  if (!list.length) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: 14, marginTop: 18 }}>
      {list.map((c, i) => (
        <div key={i} style={{ padding: 18, borderRadius: 20, background: "rgba(255,255,255,0.74)", border: "1px solid rgba(0,0,0,0.08)", boxShadow: "0 8px 20px rgba(0,0,0,0.04)", minHeight: 140 }}>
          {c.badge ? <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 999, background: "rgba(0,0,0,0.06)", fontWeight: 950, marginBottom: 12 }}>{safeTrim(c.badge, 10)}</div> : null}
          <div style={{ fontSize: 26, fontWeight: 950, lineHeight: 1.05, marginBottom: 10 }}>{safeTrim(c.title, 60)}</div>
          <div style={{ fontSize: 18, fontWeight: 850, opacity: 0.72, lineHeight: 1.25 }}>{safeTrim(c.body, 140)}</div>
        </div>
      ))}
    </div>
  );
}

function Callout({ text, variant }: { text: string; variant?: string }) {
  const colorMap: Record<string, { bg: string; border: string }> = {
    hint: { bg: "rgba(59, 130, 246, 0.07)", border: "rgba(59, 130, 246, 0.14)" },
    challenge: { bg: "rgba(245, 158, 11, 0.08)", border: "rgba(245, 158, 11, 0.18)" },
    signal: { bg: "rgba(168, 85, 247, 0.07)", border: "rgba(168, 85, 247, 0.14)" },
    observation: { bg: "rgba(34, 197, 94, 0.07)", border: "rgba(34, 197, 94, 0.14)" },
    default: { bg: "rgba(98, 84, 255, 0.07)", border: "rgba(98, 84, 255, 0.14)" },
  };
  const colors = colorMap[variant || "default"] || colorMap.default;
  return (
    <div style={{ marginTop: 18, padding: 18, borderRadius: 18, background: colors.bg, border: `1px solid ${colors.border}`, fontSize: 18, fontWeight: 900, lineHeight: 1.25 }}>
      {safeTrim(text, 200)}
    </div>
  );
}

// ── DT-specific block renderers ──────────────────────────

function AssumptionBlock({ items }: { items: Array<{ text: string; status: "active" | "confirmed" | "discarded"; note?: string }> }) {
  const list = Array.isArray(items) ? items.slice(0, 10) : [];
  if (!list.length) return null;

  return (
    <div style={{ marginTop: 18, padding: 18, borderRadius: 20, background: "rgba(255,255,255,0.72)", border: "1px solid rgba(0,0,0,0.08)", boxShadow: "0 8px 20px rgba(0,0,0,0.04)" }}>
      <div style={{ fontWeight: 950, marginBottom: 12, fontSize: 16, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 14, opacity: 0.5, textTransform: "uppercase", letterSpacing: 1 }}>Aannames</span>
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {list.map((it, i) => {
          const isDiscarded = it.status === "discarded";
          const isConfirmed = it.status === "confirmed";
          const bgColor = isDiscarded
            ? "rgba(239, 68, 68, 0.06)"
            : isConfirmed
              ? "rgba(34, 197, 94, 0.08)"
              : "rgba(59, 130, 246, 0.06)";
          const borderColor = isDiscarded
            ? "rgba(239, 68, 68, 0.15)"
            : isConfirmed
              ? "rgba(34, 197, 94, 0.2)"
              : "rgba(59, 130, 246, 0.12)";

          return (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 14px", borderRadius: 14, background: bgColor, border: `1px solid ${borderColor}`, transition: "all 0.3s ease" }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 900, flexShrink: 0, background: isDiscarded ? "rgba(239, 68, 68, 0.15)" : isConfirmed ? "rgba(34, 197, 94, 0.15)" : "rgba(59, 130, 246, 0.12)", color: isDiscarded ? "#dc2626" : isConfirmed ? "#16a34a" : "#2563eb" }}>
                {isConfirmed ? "V" : isDiscarded ? "X" : "?"}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 900, opacity: isDiscarded ? 0.5 : 0.92, textDecoration: isDiscarded ? "line-through" : "none", lineHeight: 1.3 }}>
                  {safeTrim(it.text, 120)}
                </div>
                {it.note ? <div style={{ marginTop: 4, fontSize: 13, fontWeight: 800, opacity: 0.55, fontStyle: "italic" }}>{safeTrim(it.note, 140)}</div> : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ClueBlock({ items }: { items: Array<{ text: string; unlocked: boolean; source: string }> }) {
  const list = Array.isArray(items) ? items.slice(0, 8) : [];
  if (!list.length) return null;

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontWeight: 950, marginBottom: 12, fontSize: 14, opacity: 0.5, textTransform: "uppercase", letterSpacing: 1 }}>
        Aanwijzingen
      </div>
      <div style={{ display: "grid", gap: 10 }}>
        {list.map((clue, i) => (
          <div key={i} style={{ padding: "14px 16px", borderRadius: 16, background: clue.unlocked ? "rgba(34, 197, 94, 0.06)" : "rgba(0,0,0,0.03)", border: `1px solid ${clue.unlocked ? "rgba(34, 197, 94, 0.18)" : "rgba(0,0,0,0.06)"}`, transition: "all 0.3s ease" }}>
            {clue.unlocked ? (
              <>
                <div style={{ fontSize: 16, fontWeight: 900, lineHeight: 1.3 }}>{safeTrim(clue.text, 140)}</div>
                <div style={{ marginTop: 6, fontSize: 13, fontWeight: 800, opacity: 0.5 }}>{safeTrim(clue.source, 80)}</div>
              </>
            ) : (
              <div style={{ fontSize: 15, fontWeight: 800, opacity: 0.35, fontStyle: "italic" }}>
                Stel vragen om deze aanwijzing te ontdekken...
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ConsequenceBlock({ text, success, explanation }: { text: string; success: boolean; explanation: string }) {
  return (
    <div style={{ marginTop: 18, padding: 20, borderRadius: 20, background: success ? "rgba(34, 197, 94, 0.06)" : "rgba(245, 158, 11, 0.06)", border: `1px solid ${success ? "rgba(34, 197, 94, 0.18)" : "rgba(245, 158, 11, 0.18)"}`, boxShadow: "0 8px 20px rgba(0,0,0,0.04)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{ width: 32, height: 32, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 16, background: success ? "rgba(34, 197, 94, 0.15)" : "rgba(245, 158, 11, 0.15)", color: success ? "#16a34a" : "#d97706" }}>
          {success ? "V" : "!"}
        </div>
        <div style={{ fontSize: 14, fontWeight: 900, textTransform: "uppercase", letterSpacing: 1, opacity: 0.6 }}>
          {success ? "Gelukt" : "Gevolg"}
        </div>
      </div>
      <div style={{ fontSize: 18, fontWeight: 900, lineHeight: 1.3, marginBottom: 10 }}>{safeTrim(text, 200)}</div>
      <div style={{ fontSize: 15, fontWeight: 850, opacity: 0.7, lineHeight: 1.4 }}>{safeTrim(explanation, 200)}</div>
    </div>
  );
}

function ReflectionBlock({ learned, missed, principle }: { learned: string[]; missed: string[]; principle: string }) {
  const learnedList = safeArray(learned, 5, 100);
  const missedList = safeArray(missed, 5, 100);

  return (
    <div style={{ marginTop: 18, display: "grid", gap: 14 }}>
      {/* What you learned */}
      {learnedList.length > 0 && (
        <div style={{ padding: 18, borderRadius: 18, background: "rgba(34, 197, 94, 0.06)", border: "1px solid rgba(34, 197, 94, 0.15)" }}>
          <div style={{ fontSize: 14, fontWeight: 900, textTransform: "uppercase", letterSpacing: 1, opacity: 0.6, marginBottom: 10, color: "#16a34a" }}>
            Wat je hebt geleerd
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {learnedList.map((item, i) => (
              <div key={i} style={{ fontSize: 16, fontWeight: 900, lineHeight: 1.3, paddingLeft: 12, borderLeft: "3px solid rgba(34, 197, 94, 0.3)" }}>
                {item}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* What you could have done */}
      {missedList.length > 0 && (
        <div style={{ padding: 18, borderRadius: 18, background: "rgba(59, 130, 246, 0.06)", border: "1px solid rgba(59, 130, 246, 0.12)" }}>
          <div style={{ fontSize: 14, fontWeight: 900, textTransform: "uppercase", letterSpacing: 1, opacity: 0.6, marginBottom: 10, color: "#2563eb" }}>
            Tip voor volgende keer
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {missedList.map((item, i) => (
              <div key={i} style={{ fontSize: 16, fontWeight: 900, lineHeight: 1.3, paddingLeft: 12, borderLeft: "3px solid rgba(59, 130, 246, 0.2)" }}>
                {item}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Design thinking principle */}
      {principle && (
        <div style={{ padding: 18, borderRadius: 18, background: "rgba(168, 85, 247, 0.06)", border: "1px solid rgba(168, 85, 247, 0.12)" }}>
          <div style={{ fontSize: 14, fontWeight: 900, textTransform: "uppercase", letterSpacing: 1, opacity: 0.6, marginBottom: 10, color: "#7c3aed" }}>
            Design Thinking Principe
          </div>
          <div style={{ fontSize: 18, fontWeight: 950, lineHeight: 1.3 }}>{safeTrim(principle, 200)}</div>
        </div>
      )}
    </div>
  );
}

function NextCheckBlock({ text }: { text: string }) {
  return (
    <div style={{ marginTop: 14, padding: "12px 16px", borderRadius: 14, background: "rgba(59, 130, 246, 0.06)", border: "1px solid rgba(59, 130, 246, 0.1)", display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ width: 24, height: 24, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 13, background: "rgba(59, 130, 246, 0.12)", color: "#2563eb", flexShrink: 0 }}>
        {">"}
      </div>
      <div style={{ fontSize: 15, fontWeight: 850, opacity: 0.8, lineHeight: 1.3 }}>{safeTrim(text, 140)}</div>
    </div>
  );
}

// ── Block dispatcher ─────────────────────────────────────
// Registry pattern — extensible by future profiles.

function renderBlock(block: any, index: number) {
  if (!block || typeof block !== "object") return null;

  const blockType = block.type || "";
  const content = block.content || block;

  switch (blockType) {
    case "hero":
      return (
        <div key={index}>
          <div style={{ fontSize: 40, fontWeight: 950, lineHeight: 1.05 }}>{safeTrim(content.title || "", 90)}</div>
          {content.sub ? <div style={{ marginTop: 10, fontSize: 16, fontWeight: 850, opacity: 0.7, lineHeight: 1.3 }}>{safeTrim(content.sub, 140)}</div> : null}
        </div>
      );

    case "chiprow":
      return <ChipRow key={index} chips={content.chips || []} />;

    case "fill":
      return <FillCard key={index} title={content.title || "Jij vult in"} lines={content.lines || []} />;

    case "checklist":
      return <Checklist key={index} title={content.title || "Lijst"} items={content.items || []} />;

    case "grid":
      return <Grid key={index} columns={content.columns || 3} items={content.items || []} />;

    case "callout":
      return <Callout key={index} text={safeTrim(content.text || "", 200)} variant={content.variant} />;

    // DT-specific
    case "assumption":
      return <AssumptionBlock key={index} items={content.items || []} />;

    case "clue":
      return <ClueBlock key={index} items={content.items || []} />;

    case "consequence":
      return <ConsequenceBlock key={index} text={content.text || ""} success={!!content.success} explanation={content.explanation || ""} />;

    case "reflection":
      return <ReflectionBlock key={index} learned={content.learned || []} missed={content.missed || []} principle={content.principle || ""} />;

    case "nextcheck":
  return <NextCheckBlock key={index} text={content.text || ""} />;

    default:
      return null;
  }
}

// ── Main component ───────────────────────────────────────

export default function DigiBoardPanel({
  systemPrompt,
  messages,
  digiboardUrl = DEFAULT_DIGIBOARD_URL,
  lessonTitleFallback = "Les",
  boardOverride = null,
  forcedMode,
  profileId,
  gamePhase,
    activeLesson = null,   // ✅ add this

}: Props) {



const parsed = React.useMemo(() => parseLessonTitleAndTopics(systemPrompt), [systemPrompt]);
const lessonTitle = activeLesson?.title || parsed.lessonTitle;
const topics = activeLesson?.introBubbles || parsed.topics;



  const visible = React.useMemo(() => messages.filter((m) => m.role !== "system"), [messages]);





  const lastAssistant = React.useMemo(() => {
    const last = [...visible].reverse().find((m) => m.role === "assistant");
    return last?.content || "";
  }, [visible]);

  const { explanation, question } = React.useMemo(() => splitExplanationAndQuestion(lastAssistant), [lastAssistant]);

  // DT profiles always use explanation mode (never split into question worksheet)
  const isDTProfile = profileId === "design-thinking";

const hasUserSpoken = React.useMemo(
  () => visible.some((m) => m.role === "user"),
  [visible]
);

const playbackActive = !!getPlaybackState();

const lastAssistantClean = React.useMemo(() => clean(lastAssistant), [lastAssistant]);

// DT should stay in intro while Bella is still doing onboarding/welcome.
// We only leave intro after the student has spoken AND the assistant is no longer asking "klaar?"-type questions.
const dtStillOnboarding = React.useMemo(() => {
  if (!isDTProfile) return false;
  if (!lastAssistantClean) return true;

  // Treat the full message as onboarding if it is basically a "ready?" prompt.
  if (isOnboardingQuestion(lastAssistantClean)) return true;

  // Also treat it as onboarding if the last meaningful question is an onboarding question.
  const q = extractLastMeaningfulQuestion(lastAssistantClean);
  if (q && isOnboardingQuestion(q)) return true;

  return false;
}, [isDTProfile, lastAssistantClean]);

const showIntro = forcedMode
  ? forcedMode === "intro"
  : isDTProfile
    ? (!playbackActive && (!hasUserSpoken || dtStillOnboarding))
    : (!playbackActive && !hasUserSpoken);



  React.useEffect(() => {
  if (!showIntro) return;

 console.log("[DigiBoard intro debug]\n" + JSON.stringify({
  profileId,
  isDTProfile,
  forcedMode,
  gamePhase,
  showIntro,
  playbackActive,
  activeLessonTitle: activeLesson?.title,
  activeLessonIntroBubbles: (activeLesson as any)?.introBubbles,
  topics,
  parsedTopics: parsed.topics,
  lessonTitle,
  parsedLessonTitle: parsed.lessonTitle,
}, null, 2));
}, [
  showIntro,
  profileId,
  isDTProfile,
  forcedMode,
  gamePhase,
  playbackActive,
  activeLesson,
  topics,
  parsed.topics,
  lessonTitle,
  parsed.lessonTitle,
]);


  const mode: "intro" | "question" | "explanation" = React.useMemo(() => {
    if (forcedMode) return forcedMode;
    if (showIntro) return "intro";
    if (isDTProfile) return "explanation"; // DT never does "question worksheet"
    if (question && !isOnboardingQuestion(question)) return "question";
    return "explanation";
  }, [forcedMode, showIntro, question, isDTProfile]);



  const [board, setBoard] = React.useState<DigiBoard>(null);
  const [loading, setLoading] = React.useState(false);

const [playbackTick, setPlaybackTick] = React.useState(0);

React.useEffect(() => {
  const onPlayback = () => setPlaybackTick((t) => t + 1);
  window.addEventListener("tt-playback", onPlayback);
  return () => window.removeEventListener("tt-playback", onPlayback);
}, []);



React.useEffect(() => {
  // When playback starts/advances, refresh the board
  refresh();
  // NOTE: do NOT add eslint-disable comments (your setup doesn't have that rule)
}, [playbackTick]);

void playbackTick;

  const reqIdRef = React.useRef(0);
  const abortRef = React.useRef<AbortController | null>(null);

  function boardWindow(max = 10) {
    return visible.slice(Math.max(0, visible.length - max));
  }

  async function refresh() {
    // If game/engine passes a board, render it directly (avatar-driven)
    if (boardOverride) {
      setBoard(boardOverride);
      setLoading(false);
      return;
    }



    if (mode === "intro") {
      setBoard(null);
      setLoading(false);
      return;
    }



// ✅ If lesson is active, render board from scripted playback state (SRT-synced)
if (activeLesson) {
  try {
    const localBoard = await getCTBoardForPlayback(activeLesson);
    setBoard((localBoard as any) ?? null);
  } finally {
    setLoading(false);
  }
  return;
}



    // Pipeline A: fetch from digiboard.php (KCV and legacy profiles)
    const reqId = ++reqIdRef.current;

    if (abortRef.current) {
      try { abortRef.current.abort(); } catch {}
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);

    try {
      const res = await fetch(digiboardUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
  systemPrompt: isDTProfile ? "" : systemPrompt,
  messages: isDTProfile ? [] : boardWindow(10),        // ✅ DT: no chat history
  assistantLast: lastAssistant,

  profileId: isDTProfile ? "design-thinking" : profileId,
  boardMode: isDTProfile ? "dt_strict" : undefined,
  gamePhase,

  mode: isDTProfile ? "explanation" : (mode === "question" ? "question" : "explanation"),
  extractedQuestion: isDTProfile ? "" : (question || ""),

  // ✅ DT: ONLY summarize what Bella literally said
  extractedExplanation: isDTProfile ? lastAssistant : (explanation || ""),
}),

      });

      const data = await res.json().catch(() => null);
      if (reqId !== reqIdRef.current) return;
      if (!res.ok || !data?.ok) throw new Error(data?.error || "digiboard error");

      setBoard(data.board ?? null);
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      if (reqId !== reqIdRef.current) return;

      // ✅ DT: never show template fallback boards
  if (isDTProfile) {
    setBoard(null);
    return;
  }

      // Fallback boards (existing behavior)
      if (mode === "question" && question) {
        setBoard({
          version: 2,
          scene: { title: lessonTitle || lessonTitleFallback },
          meta: { mode: "question" },
          blocks: [
            { type: "hero", title: "Vul de kernpunten in", sub: "Schrijf in steekwoorden." },
            { type: "chiprow", chips: ["rol", "handeling", "effect"] },
            { type: "fill", title: "Jij vult in", lines: ["Rol: ____", "Handeling: ____", "Effect: ____"] },
          ],
        });
      } else {
        setBoard({
          version: 2,
          scene: { title: lessonTitle || lessonTitleFallback },
          meta: { mode: "explanation" },
          blocks: [
            { type: "grid", columns: 3, items: [{ badge: "1", title: "Wat zie je?", body: "____" }, { badge: "2", title: "Signalen", body: "____" }, { badge: "3", title: "Vragen", body: "____" }] },
            { type: "fill", title: "Jij zegt nu", lines: ["Observatie: ____", "Vraag: ____"] },
          ],
        });
      }
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }

  React.useEffect(() => {
    refresh();
  }, [mode, question, explanation, lastAssistant, digiboardUrl, boardOverride]);

  React.useEffect(() => {
  // ✅ When switching profile/lesson prompt, do not keep old board around
  setBoard(null);
  setLoading(false);

  // Abort any in-flight digiboard request
  if (abortRef.current) {
    try { abortRef.current.abort(); } catch {}
  }
}, [profileId, systemPrompt, digiboardUrl]);

  // ── RENDER: INTRO ─────────────────────────────────

  if (showIntro) {
    return (
      <div>
        <div style={{ fontSize: 46, fontWeight: 950, lineHeight: 1.05, marginBottom: 14 }}>
          {safeTrim(isDTProfile ? "Design Thinking Missie" : (lessonTitle || lessonTitleFallback), 80)}
        </div>
       {isDTProfile ? (
  <div style={{ display: "grid", gap: 12 }}>
    {/* A richer DT intro “overview” board */}
    <div style={{ marginTop: 6 }}>
      <ChipRow chips={["Missie", "Signalen", "Aannames", "Probleem", "Testen"]} />
    </div>

    <Callout
      text='Je gaat een mysterie oplossen zoals een echte ontwerper: eerst begrijpen, dan pas testen.'
      variant="challenge"
    />

    <Grid
      columns={3}
      items={[
        { badge: "1", title: "Kijken", body: "Wat gebeurt er echt? Welke signalen zie je?" },
        { badge: "2", title: "Vragen", body: "Welke info mis je nog? Welke vraag ga je stellen?" },
        { badge: "3", title: "Testen", body: "Probeer iets. Kijk wat het gevolg is." },
      ]}
    />

    
  </div>
) : (
  topics && topics.length ? <TopicChips topics={topics} /> : null
  
)}
      </div>
    );
  }



  // ── RENDER: QUESTION MODE (KCV) ───────────────────

  if (mode === "question") {
    const chips = board?.blocks?.find((b) => b.type === "chiprow") as any;
    const fill = board?.blocks?.find((b) => b.type === "fill") as any;

    return (
      <div>
        <div style={{ fontSize: 44, fontWeight: 950, lineHeight: 1.05 }}>
          {safeTrim(question || "", 220)}
        </div>
        {loading ? <div style={{ marginTop: 10, opacity: 0.6, fontWeight: 800 }}>Bord wordt klaargezet</div> : null}
        {chips?.chips ? <ChipRow chips={chips.chips} /> : null}
        {fill?.lines ? <FillCard title={fill.title || "Jij vult in"} lines={fill.lines} /> : null}
      </div>
    );
  }

  // ── RENDER: EXPLANATION MODE (KCV + generic) ──────

  return (
    <div>
      {loading ? <div style={{ opacity: 0.6, fontWeight: 800 }}>Ik zet dit op je bord</div> : null}

      {board ? (
        <div style={{ display: "grid", gap: 4 }}>
          {board.blocks.map((block, i) => renderBlock(block, i))}
        </div>
      ) : (
        <div style={{ opacity: 0.7, fontWeight: 850 }}>Nog niets om te tonen</div>
      )}
    </div>
    
  );
}
